import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FaPlay } from 'react-icons/fa'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import twoPhotos from '/assets/samu/twoPhoto.webp'
import fourPhotos from '/assets/samu/fourPhotos.webp'
import { RiArrowGoBackLine } from 'react-icons/ri'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, transaction } = useSessionStateStore()
  const { config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages.checkout
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)

  // States
  const [quantity, setQuantity] = useState(1)

  // Get print packages
  const printPackages = [
    {
      id: '2 Strips',
      quantity: 1,
      price:
        config?.settings.packages?.find((p) => p.type === 'print' && p.quantity === 1)?.price ||
        35000,
      illustrationImgUrl: twoPhotos
    },
    {
      id: '4 Strips',
      quantity: 2,
      price:
        config?.settings.packages?.find((p) => p.type === 'print' && p.quantity === 2)?.price ||
        60000,
      illustrationImgUrl: fourPhotos
    }
  ]

  const calculateDiscount = (basePrice: number): number => {
    const discountPercentage = transaction.discount || 0
    return Math.floor((basePrice * discountPercentage) / 100)
  }

  const calculateTotal = (packagePrice: number): number => {
    const discount = calculateDiscount(packagePrice)
    return Math.max(0, packagePrice - discount)
  }

  const continueToPayment = async (): Promise<void> => {
    const selectedPrint = printPackages[quantity === 1 ? 0 : 1]
    const finalDiscount = calculateDiscount(selectedPrint.price)

    setSessionState({
      userPreferences: {
        copies: selectedPrint.quantity
      },
      transaction: {
        amount: calculateTotal(selectedPrint.price),
        discount: finalDiscount,
        selectedPackages: [
          {
            type: 'print',
            quantity: selectedPrint.quantity,
            price: selectedPrint.price
          }
        ]
      },
      timeline: {
        activityLogs: [
          {
            page: 'checkout',
            type: 'selection',
            action: 'complete',
            details: {
              copies: selectedPrint.quantity,
              amount: calculateTotal(selectedPrint.price),
              discount: finalDiscount
            },
            timestamp: new Date()
          }
        ]
      }
    })
    if (calculateTotal(selectedPrint.price) > 0) {
      navigate(`/${projectId}/payment`)
    } else {
      navigate(`/${projectId}/capture`)
    }
  }

  const handleQuantityChange = (newQuantity: number): void => {
    if (newQuantity >= 1 && newQuantity <= 2) {
      setQuantity(newQuantity)
    }
  }

  const selectedPackage = printPackages[quantity === 1 ? 0 : 1]
  const total = calculateTotal(selectedPackage.price)

  const cachedBackground = useCachedBackgroundImage('/assets/samu/samu-checkout.webp')
  const cachedLogo = useCachedImage('/assets/samu/samu-logo.png')
  const cachedTwoPhotos = useCachedImage(twoPhotos)
  const cachedFourPhotos = useCachedImage(fourPhotos)

  return (
    <div
      className="relative w-full h-full bg-cover bg-center"
      style={{ backgroundImage: `url(${cachedBackground})` }}
    >
      <img alt="Samu Logo" src={cachedLogo} width={240} height={240} className="absolute right-6" />
      <div className="absolute z-[5] left-[50px] top-[50px]">
        <button
          onClick={() => navigate(`/${projectId}/filter`)}
          className="flex items-center gap-3 rounded-full bg-carnabyTan px-6 py-3 text-juicyMango hover:opacity-90 transition-opacity"
        >
          <RiArrowGoBackLine size={36} />
          <span className="font-body-samu text-3xl">Back</span>
        </button>
      </div>
      <div className="absolute left-[55%] top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
        <h2 className="font-deco-samu text-foundationWhite text-7xl mb-12">
          {quantity === 1 ? '2 Strips' : '4 Strips'}
        </h2>
        <img
          className="w-auto h-[600px] object-contain mb-12"
          src={
            selectedPackage.illustrationImgUrl === twoPhotos ? cachedTwoPhotos : cachedFourPhotos
          }
          alt={selectedPackage.id}
        />
        <div className="flex flex-col items-center gap-4">
          <button
            onClick={() => handleQuantityChange(quantity + 1)}
            className={`rounded-full bg-foundationWhite p-4 hover:opacity-90 transition-opacity
            ${quantity >= 2 ? 'hidden' : 'block'}`}
            aria-label="Increase quantity"
            disabled={quantity >= 2}
          >
            <FaPlay className="text-poppyGlow w-6 h-6 -rotate-90" />
          </button>
          <button
            onClick={() => handleQuantityChange(quantity - 1)}
            className={`rounded-full bg-foundationWhite p-4 hover:opacity-90 transition-opacity
            ${quantity <= 1 ? 'hidden' : 'block'}`}
            aria-label="Decrease quantity"
            disabled={quantity <= 1}
          >
            <FaPlay className="text-poppyGlow w-6 h-6 rotate-90" />
          </button>
        </div>
      </div>

      <div className="absolute bottom-48 right-24 flex flex-col items-center gap-8">
        <div className="text-right">
          {transaction.discount > 0 && (
            <p className="font-deco-samu text-6xl text-poppyGlow line-through mb-2">
              {formatAmount(selectedPackage.price, projectCurrency)}
            </p>
          )}
          <p className="font-deco-samu text-6xl text-poppyGlow">
            {formatAmount(total, projectCurrency)}
          </p>
        </div>
        <button
          onClick={continueToPayment}
          className="font-body-samu rounded-lg bg-carnabyTan text-white px-20 tracking-widest text-4xl py-8 transition-transform transform shadow-xl hover:brightness-110 active:brightness-110"
        >
          Payment
        </button>
      </div>
    </div>
  )
}

export default CheckoutPage
